import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, Video, Loader2, FileText,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useInterviewSimSession } from '../hooks/cabin/useInterviewSimSession';
import { useAuthedVideo } from '../hooks/cabin/useAuthedVideo';
import { getInitials } from '../utils/getInitials';

// [3.2 fix] Kategori key'lerini ceviren yardimci
function catLabel(category, t) {
  if (!category) return t('interview.sim.cat.general') || 'General';
  const key = `interview.sim.cat.${category}`;
  const translated = t(key);
  if (translated && translated !== key) return translated;
  return t('interview.sim.cat.general') || 'General';
}

// ── Student video player
function StudentVideoPlayer({ sessionId, t }) {
  const { videoUrl, loading: videoLoading, error: videoError } = useAuthedVideo(sessionId, 'student');

  if (videoLoading) {
    return (
      <div className="bg-gray-900 rounded-2xl border-2 border-gray-700 aspect-video flex items-center justify-center">
        <div className="text-center text-gray-400">
          <Loader2 size={24} className="mx-auto mb-2 animate-spin opacity-60" aria-hidden="true" />
          <p className="text-xs">{t('instructor.reviews.video_loading')}</p>
        </div>
      </div>
    );
  }

  if (videoError || !videoUrl) return null;

  return (
    /* Branded video container: accent-color applies to native controls (range/progress thumb) in modern browsers */
    <div
      className="rounded-2xl overflow-hidden border-2 aspect-video"
      style={{
        background: '#0c0f1a',
        borderColor: 'var(--brand-primary)',
        /* rgba fallback for Safari <16: --brand-primary is #1D6FA4, 25% opacity */
        boxShadow: '0 0 0 2px rgba(29, 111, 164, 0.25)',
      }}
    >
      <video
        src={videoUrl}
        controls
        playsInline
        className="w-full h-full object-contain"
        aria-label={t('instructor.reviews.video_label')}
        style={{ accentColor: 'var(--brand-primary)' }}
      />
    </div>
  );
}

/**
 * CabinInterviewSessionDetail — /programs/kabin-memurlugu/interview/session/:sessionId
 *
 * Ekran 5: Öğrencinin oturum detayını tam ekranda gösterir.
 * CabinInterviewHistory'deki "İncele" aksiyonu buraya navigate eder.
 * Tarayıcı geri tuşu ve ArrowLeft butonu history listesine döner.
 */
export default function CabinInterviewSessionDetail() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const { session, loading } = useInterviewSimSession(sessionId);

  const isScored = session?.status === 'scored';

  const handleBack = () => {
    navigate('/programs/kabin-memurlugu/interview/history');
  };

  const studentInitials = getInitials(session?.user_name || '');

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#0E1626' }}>
      {/* Proto full-screen header */}
      <header
        style={{
          height: 66, flexShrink: 0,
          background: '#13233F',
          borderBottom: '1px solid rgba(255,255,255,.08)',
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '0 24px', color: '#fff',
        }}
      >
        <button
          type="button"
          onClick={handleBack}
          aria-label={t('cabin.interview.text.back')}
          style={{
            width: 40, height: 40, borderRadius: 11,
            border: '1px solid rgba(255,255,255,.14)',
            background: 'rgba(255,255,255,.06)',
            color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>

        {session && (
          <div
            aria-hidden="true"
            style={{
              width: 38, height: 38, borderRadius: 11,
              background: 'linear-gradient(135deg,#1F3357,#27406B)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 14, flexShrink: 0,
            }}
          >
            {studentInitials}
          </div>
        )}

        <div>
          <div style={{ fontFamily: "'Fredoka','Nunito',sans-serif", fontWeight: 600, fontSize: 17, lineHeight: 1.1, color: '#fff' }}>
            {t('cabin.interview.history.detail_title')}
          </div>
          <div style={{ fontSize: 12.5, color: '#9FB0C6', marginTop: 1 }}>
            {isScored ? t('interview.sim.status_scored') : t('interview.sim.status_pending')}
          </div>
        </div>

        {session && isScored && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ background: 'rgba(31,154,77,.2)', color: '#4ADE80', fontWeight: 800, fontSize: 12.5, padding: '6px 14px', borderRadius: 99 }}>
              {t('interview.sim.status_scored')}
            </span>
          </div>
        )}
        {session && !isScored && (
          <div style={{ marginLeft: 'auto' }}>
            <span style={{ background: 'rgba(249,168,37,.15)', color: '#FBBF24', fontWeight: 800, fontSize: 12.5, padding: '6px 14px', borderRadius: 99 }}>
              {t('interview.sim.status_pending')}
            </span>
          </div>
        )}
      </header>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !session ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center', color: '#9FB0C6' }}>
            <Video size={36} style={{ opacity: 0.4 }} aria-hidden="true" />
            <p style={{ fontWeight: 600 }}>{t('cabin.interview.history.no_detail')}</p>
            <button
              type="button"
              onClick={handleBack}
              style={{ padding: '10px 20px', borderRadius: 11, border: 'none', background: '#14B4CC', color: '#fff', fontFamily: 'inherit', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
            >
              {t('cabin.interview.text.back')}
            </button>
          </div>
        ) : isScored ? (
          /* Düzen 2 — Öğrenci Tam Ekran Playback:
             Büyük video ortalı/geniş (max 920px), altta sorular chip listesi.
             Sağ skor paneli YOK — skorlar CabinReadinessReport'ta görülebilir. */
          <div
            style={{
              flex: 1, minWidth: 0, overflowY: 'auto',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center',
              padding: '32px 28px 48px',
              gap: 24,
            }}
          >
            {/* Video — tam genişlik, büyük, ortalı */}
            <div style={{ width: '100%', maxWidth: 920 }}>
              <StudentVideoPlayer sessionId={sessionId} t={t} />
            </div>

            {/* Sorular — chip / liste, video genişliğiyle hizalı */}
            {session.questions && session.questions.length > 0 && (
              <div style={{ width: '100%', maxWidth: 920 }}>
                <p
                  style={{
                    fontSize: 11, fontWeight: 800, letterSpacing: '.6px',
                    color: '#4A6080', textTransform: 'uppercase',
                    marginBottom: 12,
                  }}
                >
                  {t('cabin.interview.video.question_list')}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {session.questions.map((q, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        background: 'rgba(255,255,255,.06)',
                        border: '1px solid rgba(255,255,255,.08)',
                        borderRadius: 12,
                        padding: '10px 14px',
                      }}
                    >
                      <span
                        style={{
                          minWidth: 22, height: 22, borderRadius: 7,
                          background: 'var(--brand-primary,#1D6FA4)',
                          color: '#fff', fontSize: 11, fontWeight: 800,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, marginTop: 1,
                        }}
                      >
                        {i + 1}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13.5, fontWeight: 600, color: '#D0DBE8', lineHeight: 1.45, margin: 0 }}>
                          {q.question}
                        </p>
                        {q.category && (
                          <span
                            style={{
                              display: 'inline-block', marginTop: 4,
                              fontSize: 11, fontWeight: 700,
                              background: 'rgba(255,255,255,.07)',
                              border: '1px solid rgba(255,255,255,.1)',
                              borderRadius: 99, padding: '2px 10px', color: '#7E9AB8',
                            }}
                          >
                            {catLabel(q.category, t)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Pending — centered in dark bg */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, textAlign: 'center', padding: 40 }}>
            <div style={{ width: 64, height: 64, borderRadius: 20, background: 'rgba(251,191,36,.15)', border: '1px solid rgba(251,191,36,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={28} style={{ color: '#FBBF24' }} aria-hidden="true" />
            </div>
            <div>
              <h3 style={{ fontFamily: "'Fredoka','Nunito',sans-serif", fontWeight: 600, fontSize: 22, color: '#fff', marginBottom: 6 }}>
                {t('interview.sim.status_pending')}
              </h3>
              <p style={{ fontSize: 14, color: '#9FB0C6', maxWidth: 320, margin: '0 auto', lineHeight: 1.6 }}>
                {t('interview.sim.pending_desc')}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#7E92AE', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 12, padding: '10px 18px' }}>
              <FileText size={13} aria-hidden="true" />
              <span>{t('cabin.interview.history.pending_hint') || t('interview.sim.pending_desc')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
